import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  TextField, 
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Search as SearchIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import MessageButton from '../components/MessageButton';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface User {
  id: number;
  name: string;
  email: string;
  EmployeeProfile?: {
    id: number;
  };
}

interface TransferRequest { 
  id: number; 
  userId: number;
  profession: string; 
  currentLocation: string; 
  targetLocations: string[];
  notes?: string;
  status: string;
  createdAt: string;
  User: User;
}

const TransferHubSearch: React.FC = () => {
  const { token } = useAuth();
  const [profession, setProfession] = useState('');
  const [location, setLocation] = useState('');
  const [results, setResults] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const run = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      if (profession.trim()) params.set('profession', profession.trim());
      if (location.trim()) params.set('location', location.trim());
      
      const res = await fetch(`${API_BASE}/transferhub/search?${params.toString()}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.ok) {
        const data = await res.json();
        setResults(data);
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Search failed');
      }
    } catch (err) {
      setError('Failed to perform search. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setProfession('');
    setLocation('');
    setResults([]);
    setError('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      run();
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Search Transfer Requests
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Find Transfer Opportunities
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Profession" 
                fullWidth 
                value={profession} 
                onChange={(e) => setProfession(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., Software Developer, Teacher"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: profession && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setProfession('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Current Location" 
                fullWidth 
                value={location} 
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., Cape Town, Johannesburg"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: location && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setLocation('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              onClick={run} 
              disabled={loading}
              startIcon={<SearchIcon />}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={clearSearch}
              startIcon={<ClearIcon />}
            >
              Clear
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Search Results ({results.length} found)
          </Typography>
          <Grid container spacing={2}>
            {results.map(request => (
              <Grid item xs={12} md={6} key={request.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="h6">
                          {request.User.name}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                      {request.profession}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Chip label={request.currentLocation} size="small" sx={{ mr: 1, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" component="span">
                        → {request.targetLocations.join(', ')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={request.status} 
                        size="small" 
                        color={request.status === 'open' ? 'success' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    {request.notes && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{request.notes}"
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => {
                          const profileId = request.User.EmployeeProfile?.id;
                          if (profileId) {
                            window.location.href = `/transferhub/profiles/${profileId}`;
                          } else {
                            alert('This user does not have a profile yet.');
                          }
                        }}
                        disabled={!request.User.EmployeeProfile?.id}
                      >
                        View Profile
                      </Button>
                      {request.User?.id && (
                        <MessageButton
                          receiverId={request.User.id}
                          receiverName={request.User.name}
                          variant="contained"
                          size="small"
                          startIcon
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* No Results */}
      {results.length === 0 && !loading && (profession || location) && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SearchIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or check back later for new transfer requests.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {results.length === 0 && !loading && !profession && !location && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SearchIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Search for Transfer Opportunities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a profession and/or location to find people looking for transfers.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TransferHubSearch;